import React, { useEffect, useRef, useState } from 'react';

import { fetchMediaAudioUrl, revokeMediaAudioUrl } from '@/utilities/mediaAudio';

interface MediaAudioPlayerProps {
  src?: string | null;
  /** Show Play but don't allow it (e.g. media row status is not 1). */
  disabled?: boolean;
  disabledTitle?: string;
  controls?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onLoadedMetadata?: React.ReactEventHandler<HTMLAudioElement>;
}

function MediaAudioPlayer({
  src,
  disabled = false,
  disabledTitle,
  controls = true,
  className,
  style,
  onLoadedMetadata,
}: MediaAudioPlayerProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const currentUrlRef = useRef<string | null>(null);

  // A changed src means a different clip: drop whatever is held.
  useEffect(() => {
    revokeMediaAudioUrl(currentUrlRef.current);
    currentUrlRef.current = null;
    setObjectUrl(null);
    setError(null);
    setLoading(false);
  }, [src]);

  useEffect(
    () => () => {
      revokeMediaAudioUrl(currentUrlRef.current);
      currentUrlRef.current = null;
    },
    [],
  );

  const load = () => {
    if (!src || disabled || loading || objectUrl) return;

    setLoading(true);
    setError(null);

    fetchMediaAudioUrl(src)
      .then((next) => {
        currentUrlRef.current = next;
        setObjectUrl(next);
      })
      .catch((err: any) => {
        setError(
          typeof err === 'string' ? err : err?.message || 'Unable to load audio',
        );
      })
      .finally(() => setLoading(false));
  };

  if (!src) return null;

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        title={disabledTitle}
        className={className}
        style={{
          border: 'none',
          background: 'none',
          padding: 0,
          cursor: 'not-allowed',
          color: '#94a3b8',
          fontSize: 12,
          ...style,
        }}
      >
        ▶ Play
      </button>
    );
  }

  if (error) {
    return (
      <span
        style={{ fontSize: 12, color: '#dc2626', cursor: 'pointer' }}
        onClick={load}
        title="Click to retry"
      >
        {error} — retry
      </span>
    );
  }

  if (!objectUrl) {
    return (
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className={className}
        style={{
          border: 'none',
          background: 'none',
          padding: 0,
          cursor: loading ? 'default' : 'pointer',
          color: '#2563eb',
          fontSize: 12,
          ...style,
        }}
      >
        {loading ? 'Loading…' : '▶ Play'}
      </button>
    );
  }

  return (
    <audio
      key={objectUrl}
      src={objectUrl}
      controls={controls}
      autoPlay
      className={className}
      style={style}
      onLoadedMetadata={onLoadedMetadata}
    />
  );
}

export default React.memo(MediaAudioPlayer);
